import { ArrowUp, Loader2 } from "lucide-react";
import * as React from "react";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { Button } from "../button";

interface ChatInputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onSend?: (message: string) => void;
  isLoading?: boolean;
  onHeightChange?: (height: number) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  className,
  onSend,
  disabled = false,
  isLoading = false,
  onHeightChange,
  ...props
}) => {
  const [input, setInput] = useState("");
  const [isMaxHeight, setIsMaxHeight] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      const minHeight = 40;
      textarea.style.height = `${minHeight}px`;
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, minHeight),
        320
      );
      textarea.style.height = `${newHeight}px`;
      setIsMaxHeight(newHeight === 320);
      if (onHeightChange) {
        onHeightChange(newHeight);
      }
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
    window.addEventListener("resize", adjustTextareaHeight);
    return () => window.removeEventListener("resize", adjustTextareaHeight);
  }, [input]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!disabled && !isLoading && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && onSend) {
        onSend(input.trim());
        setInput("");
      }
    }
  };

  return (
    <div className="relative flex items-center">
      <Textarea
        autoComplete="off"
        ref={textareaRef}
        name="message"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          adjustTextareaHeight();
        }}
        onClick={() => {
          trackEvent({ eventName: "app/doc_list/chat_input.input/click" });
        }}
        onKeyDown={handleKeyDown}
        rows={1}
        className={cn(
          "pr-12 py-2 bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-foreground disabled:cursor-not-allowed disabled:opacity-50 rounded-md resize-none min-h-[40px] max-h-[320px]",
          isMaxHeight ? "overflow-y-auto" : "overflow-hidden",
          className
        )}
        {...props}
      />
      <div className="absolute right-1 flex items-end h-full pb-1">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          input.trim() && (
            <Button
              size="icon"
              onClick={() => {
                trackEvent({
                  eventName: "app/doc_list/send_message.button/submit",
                });
                if (input.trim() && onSend) {
                  onSend(input.trim());
                  setInput("");
                }
              }}
              disabled={disabled || isLoading}
              className="w-8 h-8 bg-blue-600 hover:bg-blue-700"
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export { ChatInput };
